// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Users, MapPin, Minus, Plus, ChevronRight, LogOut, PartyPopper, User, Bell } from 'lucide-react';
import {
  getPublicRestauranteReservo,
  getPublicReservoSettings,
  listPublicSalas,
  createPublicWaitlistEntry,
  getPublicWaitlistEntryByToken,
  cancelPublicWaitlistEntryByToken,
} from '../../../../../services/publicReservoApi';
import { getApiErrorMessage } from '../../../../../utils/apiError';
import '../../styles/public-waitlist.css';

const MAX_PARTY_SIZE = 30;

interface PublicWaitlistProps {
  slug?: string;
}

type WaitlistStatus = 'waiting' | 'ready' | 'notified' | 'seated' | 'cancelled';

interface WaitlistTicket {
  id: string;
  token: string;
  name: string;
  partySize: number;
  phone: string;
  ambiente: string;
  notes: string;
  joinedAt: number;
  position: number | null;
  status: WaitlistStatus;
  whatsappOptIn: boolean;
  whatsappOptInUrl: string;
}

interface SalaOption {
  id: string | number;
  nome?: string | null;
  name?: string | null;
}

type View = 'form' | 'status' | 'left' | 'disabled';

const onlyDigits = (value: string) => value.replace(/\D/g, '');

const prettifySlug = (slug: string): string =>
  slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatPhoneBR = (value: string): string => {
  let digits = onlyDigits(value);
  if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
  digits = digits.slice(0, 11);
  if (digits.length <= 2) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

interface Country {
  code: string;
  label: string;
  dial: string;
}

const COUNTRIES: Country[] = [
  { code: 'BR', label: 'BR', dial: '55' },
  { code: 'PT', label: 'PT', dial: '351' },
  { code: 'US', label: 'US', dial: '1' },
  { code: 'AR', label: 'AR', dial: '54' },
  { code: 'UY', label: 'UY', dial: '598' },
  { code: 'PY', label: 'PY', dial: '595' },
  { code: 'CL', label: 'CL', dial: '56' },
  { code: 'CO', label: 'CO', dial: '57' },
  { code: 'PE', label: 'PE', dial: '51' },
  { code: 'MX', label: 'MX', dial: '52' },
  { code: 'ES', label: 'ES', dial: '34' },
  { code: 'IT', label: 'IT', dial: '39' },
  { code: 'FR', label: 'FR', dial: '33' },
  { code: 'GB', label: 'GB', dial: '44' },
  { code: 'DE', label: 'DE', dial: '49' },
  { code: 'AO', label: 'AO', dial: '244' },
];

const DEFAULT_COUNTRY_CODE = 'BR';

const getCountry = (code: string): Country =>
  COUNTRIES.find((country) => country.code === code) || COUNTRIES[0];

const formatPhone = (value: string, isBR: boolean): string =>
  isBR ? formatPhoneBR(value) : onlyDigits(value).slice(0, 15);

const formatTimer = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const normalizeCreatedAt = (value: unknown): number => {
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? parsed : Date.now();
};

const mapWaitlistTicket = (entry: any): WaitlistTicket => ({
  id: String(entry?.id || ''),
  token: String(entry?.public_token || ''),
  name: String(entry?.nome_cliente || ''),
  partySize: Math.max(1, Math.floor(Number(entry?.numero_pessoas) || 1)),
  phone: String(entry?.telefone_cliente || ''),
  ambiente: String(entry?.sala_nome || 'Qualquer ambiente'),
  notes: String(entry?.obs || ''),
  joinedAt: normalizeCreatedAt(entry?.created_at),
  position: Number(entry?.position || 0) > 0 ? Number(entry.position) : null,
  status: ['waiting', 'ready', 'notified', 'seated', 'cancelled'].includes(String(entry?.status))
    ? entry.status
    : 'waiting',
  whatsappOptIn: entry?.whatsapp_opt_in === true,
  whatsappOptInUrl: String(entry?.whatsapp_opt_in_url || ''),
});

const getSalaLabel = (sala: SalaOption): string => (
  String(sala?.nome || sala?.name || `Ambiente ${sala?.id}`)
);

const PublicWaitlist: React.FC<PublicWaitlistProps> = ({ slug: slugProp }) => {
  const params = useParams<{ slug_restaurante: string }>();
  const slug = slugProp || params.slug_restaurante || '';

  const [restaurantName, setRestaurantName] = useState('');
  const [view, setView] = useState<View>('form');
  const [ticket, setTicket] = useState<WaitlistTicket | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [salas, setSalas] = useState<SalaOption[]>([]);

  const [name, setName] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phone, setPhone] = useState('');
  const [selectedSalaId, setSelectedSalaId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const country = getCountry(countryCode);
  const isBR = country.code === 'BR';

  useEffect(() => {
    let active = true;

    const fetchPublicData = async () => {
      if (!slug) {
        setLoadingConfig(false);
        return;
      }

      setLoadingConfig(true);
      try {
        const [restaurant, settings, publicSalas] = await Promise.all([
          getPublicRestauranteReservo(slug),
          getPublicReservoSettings(slug),
          listPublicSalas(slug),
        ]);

        if (!active) return;

        if (restaurant?.nome) setRestaurantName(restaurant.nome);

        const enabled = settings?.reservo_usar_fila === true;
        setWaitlistEnabled(enabled);
        setSalas(Array.isArray(publicSalas) ? publicSalas : []);
        setView((current) => {
          if (!enabled) return 'disabled';
          return current === 'disabled' ? 'form' : current;
        });
      } catch (fetchError) {
        if (!active) return;
        setWaitlistEnabled(false);
        setView('disabled');
        setError('Não foi possível carregar a fila de espera deste restaurante.');
      } finally {
        if (active) setLoadingConfig(false);
      }
    };

    fetchPublicData();

    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (view !== 'status' || !ticket?.token) return undefined;

    let active = true;
    const refreshTicket = async () => {
      try {
        const updatedEntry = await getPublicWaitlistEntryByToken(ticket.token);
        if (!active) return;

        const updatedTicket = mapWaitlistTicket(updatedEntry);
        if (updatedTicket.status === 'cancelled' || updatedTicket.status === 'seated') {
          setTicket(null);
          setView('left');
          return;
        }

        setTicket(updatedTicket);
      } catch (refreshError) {
        // Mantém o último ticket visível se a atualização pontual falhar.
      }
    };

    const timer = setInterval(refreshTicket, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [ticket?.token, view]);

  const displayName = restaurantName || (slug ? prettifySlug(slug) : 'Fila de Espera');
  const firstName = useMemo(() => (ticket?.name || '').trim().split(' ')[0] || '', [ticket]);
  const elapsedMs = ticket ? now - ticket.joinedAt : 0;
  const isReady = ticket?.status === 'ready';
  const selectedSalaLabel = useMemo(() => {
    if (!selectedSalaId) return 'Qualquer ambiente';
    const selected = salas.find((sala) => String(sala.id) === String(selectedSalaId));
    return selected ? getSalaLabel(selected) : 'Qualquer ambiente';
  }, [salas, selectedSalaId]);

  const adjustPartySize = useCallback((delta: number) => {
    setPartySize((current) => Math.min(MAX_PARTY_SIZE, Math.max(1, current + delta)));
  }, []);

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    if (!waitlistEnabled || !slug) {
      setError('A fila de espera não está disponível neste momento.');
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Escreva o seu nome para entrar na fila.');
      return;
    }

    const minDigits = isBR ? 10 : 6;
    if (onlyDigits(phone).length < minDigits) {
      setError(isBR
        ? 'Informe um WhatsApp válido com DDD (ex.: 11 90000-0000).'
        : 'Informe um número de WhatsApp válido.');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createPublicWaitlistEntry(slug, {
        nome_cliente: trimmedName,
        telefone_cliente: `+${country.dial} ${phone}`,
        numero_pessoas: partySize,
        sala_id: selectedSalaId || null,
        obs: notes.trim() || null,
      });

      const nextTicket = mapWaitlistTicket({
        ...created,
        sala_nome: created?.sala_nome || selectedSalaLabel,
      });

      setTicket(nextTicket);
      setError('');
      setView('status');
    } catch (submitError) {
      setError(getApiErrorMessage(submitError, 'Não foi possível entrar na fila.'));
    } finally {
      setSubmitting(false);
    }
  }, [
    country.dial,
    isBR,
    name,
    notes,
    partySize,
    phone,
    selectedSalaId,
    selectedSalaLabel,
    slug,
    waitlistEnabled,
  ]);

  const handleLeave = useCallback(async () => {
    const token = ticket?.token;
    setSubmitting(true);
    try {
      if (token) {
        await cancelPublicWaitlistEntryByToken(token);
      }
    } catch (leaveError) {
      // Mesmo se a chamada falhar, a tela local sai da fila para não prender o usuário.
    } finally {
      setSubmitting(false);
      setView('left');
      setTicket(null);
    }
  }, [ticket?.token]);

  const handleWhatsappOptIn = useCallback(() => {
    if (!ticket?.whatsappOptInUrl) {
      setError('WhatsApp indisponível para esta fila no momento.');
      return;
    }

    window.open(ticket.whatsappOptInUrl, '_blank', 'noopener,noreferrer');
  }, [ticket?.whatsappOptInUrl]);

  const handleRejoin = useCallback(() => {
    setName('');
    setPartySize(2);
    setCountryCode(DEFAULT_COUNTRY_CODE);
    setPhone('');
    setSelectedSalaId('');
    setNotes('');
    setError('');
    setView(waitlistEnabled ? 'form' : 'disabled');
  }, [waitlistEnabled]);

  const renderDetails = (data: WaitlistTicket) => (
    <div className="pw-details">
      <div className="pw-detail-row">
        <span className="pw-detail-icon"><Users size={18} /></span>
        <span className="pw-detail-label">Pessoas</span>
        <span className="pw-detail-value">{data.partySize}</span>
      </div>
      <div className="pw-detail-row">
        <span className="pw-detail-icon"><MapPin size={18} /></span>
        <span className="pw-detail-label">Ambiente</span>
        <span className="pw-detail-value">{data.ambiente}</span>
      </div>
      {data.notes ? (
        <div className="pw-detail-row">
          <span className="pw-detail-icon"><Bell size={18} /></span>
          <span className="pw-detail-label">Observação</span>
          <span className="pw-detail-value">{data.notes}</span>
        </div>
      ) : null}
    </div>
  );

  const renderWhatsappOptIn = (data: WaitlistTicket) => (
    <div className="pw-notify">
      {data.whatsappOptIn ? (
        <div className="pw-notify-active">
          <span className="pw-notify-active-icon"><Bell size={18} /></span>
          <span className="pw-notify-active-body">
            <strong>WhatsApp ativo para esta fila</strong>
            <span>{data.phone}</span>
          </span>
        </div>
      ) : (
        <button
          type="button"
          className="pw-notify-button"
          onClick={handleWhatsappOptIn}
          disabled={!data.whatsappOptInUrl}
        >
          <Bell size={18} />
          Ser avisado pelo WhatsApp
        </button>
      )}
    </div>
  );

  return (
    <div className="public-waitlist">
      <div className="public-waitlist-card">
        <header className="pw-brand">
          <h1 className="pw-brand-name">{displayName}</h1>
        </header>

        <div className="pw-body">
          {loadingConfig && (
            <div className="pw-left">
              <div className="pw-left-icon">
                <Clock size={30} />
              </div>
              <h2 className="pw-title">Carregando fila</h2>
              <p className="pw-subtitle">Aguarde enquanto buscamos as informações do restaurante.</p>
            </div>
          )}

          {!loadingConfig && view === 'disabled' && (
            <div className="pw-left">
              <div className="pw-left-icon">
                <LogOut size={30} />
              </div>
              <h2 className="pw-title">Fila indisponível</h2>
              <p className="pw-subtitle">
                Este restaurante não está recebendo entradas pela fila de espera no momento.
              </p>
            </div>
          )}

          {!loadingConfig && view === 'form' && (
            <>
              <div className="pw-intro">
                <h2 className="pw-title">Entre na fila</h2>
                <p className="pw-subtitle">
                  Sem reserva? Entre na fila e acompanhe a sua posição por aqui.
                </p>
              </div>

              <form className="pw-form" onSubmit={handleSubmit} noValidate>
                <div className="pw-field">
                  <label htmlFor="pw-name">Seu nome</label>
                  <div className="pw-input-group">
                    <span className="pw-input-icon"><User size={18} /></span>
                    <input
                      id="pw-name"
                      className="pw-input-with-icon"
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Como podemos te chamar?"
                      maxLength={80}
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div className="pw-field">
                  <label>Quantas pessoas?</label>
                  <div className="pw-stepper" role="group" aria-label="Número de pessoas">
                    <button
                      type="button"
                      className="pw-stepper-button"
                      onClick={() => adjustPartySize(-1)}
                      disabled={partySize <= 1}
                      aria-label="Diminuir"
                    >
                      <Minus size={20} />
                    </button>
                    <span className="pw-stepper-value">
                      <Users size={18} />
                      {partySize}
                    </span>
                    <button
                      type="button"
                      className="pw-stepper-button"
                      onClick={() => adjustPartySize(1)}
                      disabled={partySize >= MAX_PARTY_SIZE}
                      aria-label="Aumentar"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>

                <div className="pw-field">
                  <label htmlFor="pw-phone">Seu WhatsApp</label>
                  <div className="pw-phone-group">
                    <select
                      className="pw-phone-country"
                      value={countryCode}
                      onChange={(event) => {
                        const nextCode = event.target.value;
                        setCountryCode(nextCode);
                        setPhone((prev) => formatPhone(prev, nextCode === 'BR'));
                      }}
                      aria-label="País"
                    >
                      {COUNTRIES.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label} +{item.dial}
                        </option>
                      ))}
                    </select>
                    <input
                      id="pw-phone"
                      className="pw-input-phone"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(event) => setPhone(formatPhone(event.target.value, isBR))}
                      placeholder={isBR ? '(11) 90000-0000' : 'Número com código de área'}
                      maxLength={18}
                      autoComplete="tel"
                    />
                  </div>
                  <small className="pw-hint">Fica registrado para o restaurante falar com você, se precisar.</small>
                </div>

                {salas.length > 1 && (
                  <div className="pw-field">
                    <label htmlFor="pw-sala">Ambiente</label>
                    <select
                      id="pw-sala"
                      className="pw-select"
                      value={selectedSalaId}
                      onChange={(event) => setSelectedSalaId(event.target.value)}
                    >
                      <option value="">Qualquer ambiente</option>
                      {salas.map((sala) => (
                        <option key={String(sala.id)} value={String(sala.id)}>
                          {getSalaLabel(sala)}
                        </option>
                      ))}
                    </select>
                    <small className="pw-hint">Opcional. Deixe em branco se qualquer ambiente servir.</small>
                  </div>
                )}

                <div className="pw-field">
                  <label htmlFor="pw-notes">Observação</label>
                  <textarea
                    id="pw-notes"
                    className="pw-textarea"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Ex.: carrinho de bebê, preferência de área, acessibilidade..."
                    maxLength={240}
                    rows={3}
                  />
                </div>

                {error && <div className="pw-error" role="alert">{error}</div>}

                <button type="submit" className="pw-submit" disabled={submitting}>
                  {submitting ? 'Entrando...' : 'Entrar na fila'}
                  <ChevronRight size={20} />
                </button>
              </form>
            </>
          )}

          {!loadingConfig && view === 'status' && ticket && !isReady && (
            <div className="pw-status">
              <div className="pw-status-greeting">
                <span className="pw-status-hello">Olá,</span>
                <span className="pw-status-name">{firstName}</span>
              </div>
              <p className="pw-status-title">Sua posição na fila</p>

              <div className="pw-ring">
                <div className="pw-ring-inner">
                  <span className="pw-ring-number">{ticket.position ?? '-'}</span>
                  <span className="pw-ring-label">na fila</span>
                </div>
              </div>

              <div className="pw-timer">
                <Clock size={18} />
                <span className="pw-timer-value">{formatTimer(elapsedMs)}</span>
                <span className="pw-timer-label">na fila</span>
              </div>

              <div className="pw-status-pill">
                <span className="pw-status-dot" />
                Aguarde ser chamado
              </div>

              {renderWhatsappOptIn(ticket)}

              {error && <div className="pw-error" role="alert">{error}</div>}

              {renderDetails(ticket)}

              <button type="button" className="pw-leave" onClick={handleLeave} disabled={submitting}>
                <LogOut size={16} />
                Sair da fila
              </button>

              <p className="pw-reassure">
                Sua posição é atualizada automaticamente. Mantenha esta página aberta ou volte por este link para acompanhar.
              </p>
            </div>
          )}

          {!loadingConfig && view === 'status' && ticket && isReady && (
            <div className="pw-status pw-status--ready">
              <div className="pw-ready-icon">
                <PartyPopper size={34} />
              </div>
              <h2 className="pw-title">É a sua vez{firstName ? `, ${firstName}` : ''}!</h2>
              <p className="pw-subtitle">
                Sua mesa está pronta no {displayName}. Dirija-se à recepção.
              </p>

              <div className="pw-status-pill is-ready">
                <span className="pw-status-dot" />
                Mesa liberada
              </div>

              {renderDetails(ticket)}

              <button type="button" className="pw-leave" onClick={handleLeave} disabled={submitting}>
                <LogOut size={16} />
                Sair da fila
              </button>

              <p className="pw-reassure">Mostre esta tela na recepção.</p>
            </div>
          )}

          {!loadingConfig && view === 'left' && (
            <div className="pw-left">
              <div className="pw-left-icon">
                <LogOut size={30} />
              </div>
              <h2 className="pw-title">Você saiu da fila</h2>
              <p className="pw-subtitle">
                Precisou desistir? Sem problema. Se mudar de ideia, é só entrar de novo.
              </p>
              <button type="button" className="pw-submit" onClick={handleRejoin}>
                Entrar na fila novamente
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicWaitlist;
